/**
 * Form field primitives (§13.4): persistent label, required/optional marker, hint BEFORE the input, correct
 * autocomplete / inputMode, inline error linked via aria-describedby + aria-invalid.
 */
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { Check } from 'lucide-react';
import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { cn } from '../cn';

interface FieldContextValue {
  id: string;
  describedBy?: string;
  invalid: boolean;
  required: boolean;
}
const FieldContext = createContext<FieldContextValue | null>(null);

export interface FieldProps {
  label: ReactNode;
  /** Shown before the input (§13.4 "hint before input"). */
  hint?: ReactNode;
  /** Inline error; identifies the issue and the correction (§13.5). */
  error?: ReactNode;
  required?: boolean;
  /** Marker text; defaults to "required" / "optional". */
  requiredLabel?: string;
  optionalLabel?: string;
  /** Override the generated id (useful for error-summary anchors). */
  id?: string;
  className?: string;
  children: ReactNode;
}

export function Field({
  label,
  hint,
  error,
  required = false,
  requiredLabel = 'required',
  optionalLabel = 'optional',
  id: idProp,
  className,
  children,
}: FieldProps) {
  const generated = useId();
  const id = idProp ?? generated;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error), required }}>
      <div className={cn('wd-field', className)} data-invalid={error ? 'true' : undefined}>
        <label className="wd-field__label" htmlFor={id}>
          <span>{label}</span>
          <span className="wd-field__marker">({required ? requiredLabel : optionalLabel})</span>
        </label>
        {hint ? (
          <div id={hintId} className="wd-field__hint">
            {hint}
          </div>
        ) : null}
        {children}
        {error ? (
          <div id={errorId} className="wd-field__error" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

/** Props that put the right keyboard on phones (§13.4 "correct autocomplete and input mode"). */
export const inputPresets = {
  name: { autoComplete: 'name', inputMode: 'text', autoCapitalize: 'words' },
  email: {
    autoComplete: 'email',
    inputMode: 'email',
    type: 'email',
    autoCapitalize: 'none',
    spellCheck: false,
  },
  tel: { autoComplete: 'tel', inputMode: 'tel', type: 'tel' },
  otp: { autoComplete: 'one-time-code', inputMode: 'numeric', pattern: '[0-9]*' },
  postal: { autoComplete: 'postal-code', inputMode: 'text' },
  handle: { autoComplete: 'off', inputMode: 'text', autoCapitalize: 'none', spellCheck: false },
  number: { inputMode: 'numeric', pattern: '[0-9]*' },
} as const satisfies Record<string, InputHTMLAttributes<HTMLInputElement>>;

export type InputProps = InputHTMLAttributes<HTMLInputElement> & { preset?: keyof typeof inputPresets };

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { preset, className, ...rest },
  ref,
) {
  const field = useFieldContext();
  const presetProps = preset ? inputPresets[preset] : {};
  return (
    <input
      ref={ref}
      id={field?.id}
      className={cn('wd-input', className)}
      aria-describedby={field?.describedBy}
      aria-invalid={field?.invalid ? true : undefined}
      aria-required={field?.required ? true : undefined}
      {...presetProps}
      {...rest}
    />
  );
});

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, className, ...rest },
  ref,
) {
  const field = useFieldContext();
  return (
    <select
      ref={ref}
      id={field?.id}
      className={cn('wd-select', className)}
      aria-describedby={field?.describedBy}
      aria-invalid={field?.invalid ? true : undefined}
      aria-required={field?.required ? true : undefined}
      {...rest}
    >
      {placeholder ? (
        <option value="" disabled>
          {placeholder}
        </option>
      ) : null}
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
});

export interface CheckboxProps {
  /** Label is part of the control: every checkbox is independent and unticked by default (§12.5 consent). */
  label: ReactNode;
  description?: ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  value?: string;
  id?: string;
  className?: string;
  error?: ReactNode;
}

export function Checkbox({
  label,
  description,
  checked,
  defaultChecked = false,
  onCheckedChange,
  disabled,
  required,
  name,
  value,
  id: idProp,
  className,
  error,
}: CheckboxProps) {
  const generated = useId();
  const id = idProp ?? generated;
  const descId = description ? `${id}-desc` : undefined;
  const errId = error ? `${id}-err` : undefined;
  return (
    <div className={cn('wd-field', className)}>
      <label className="wd-check" htmlFor={id}>
        <CheckboxPrimitive.Root
          id={id}
          className="wd-check__box"
          checked={checked}
          defaultChecked={defaultChecked}
          onCheckedChange={(v) => onCheckedChange?.(v === true)}
          disabled={disabled}
          required={required}
          name={name}
          value={value}
          aria-describedby={[descId, errId].filter(Boolean).join(' ') || undefined}
          aria-invalid={error ? true : undefined}
        >
          <CheckboxPrimitive.Indicator>
            <Check size={16} aria-hidden="true" />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        <span className="wd-check__text">
          <span>{label}</span>
          {description ? (
            <span id={descId} className="wd-field__hint" style={{ display: 'block' }}>
              {description}
            </span>
          ) : null}
        </span>
      </label>
      {error ? (
        <div id={errId} className="wd-field__error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

export interface SwitchProps {
  label: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Textual state so the setting never relies on colour alone. */
  onLabel?: string;
  offLabel?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export function Switch({
  label,
  checked,
  onCheckedChange,
  onLabel = 'On',
  offLabel = 'Off',
  disabled,
  id: idProp,
  className,
}: SwitchProps) {
  const generated = useId();
  const id = idProp ?? generated;
  return (
    <div className={cn('wd-switch', className)}>
      <label htmlFor={id}>{label}</label>
      <SwitchPrimitive.Root
        id={id}
        className="wd-switch__track"
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      >
        <SwitchPrimitive.Thumb className="wd-switch__thumb" />
      </SwitchPrimitive.Root>
      <span className="wd-switch__state" aria-hidden="true">
        {checked ? onLabel : offLabel}
      </span>
    </div>
  );
}
